import { env } from "@settings";

import { IApiFeatureFlagsResponse } from "@hooks/api/common";

type FeatureFlagsKeysFromEnv =
  | "enableAggregateOfferDocumentType"
  | "enableCorrespondenteBanqueiro"
  | "enableImageDocumentsRequirementsToUpload"
  | "enableSimulationSelectFieldInstallments"
  | "enableContractsDocumentStatus"
  | "enableContractsOrderTitle"
  | "enableValidationCRCP"
  | "enableDigitalBanks"
  | "enableDocumentsUploadType"
  | "enableDinamicMarginInss"
  | "enableHistoryCRCP";

type FeatureFlagsKeysFromApi =
  | "enableValidationCRCP"
  | "enableValidationMCB"
  | "enableDigitalBanks"
  | "enableDocumentsUploadType"
  | "enableDinamicMarginInss"
  | "enableBypassValidaCPF"
  | "enableCreateContractBff"
  | "enableIgnoreMarginError"
  | "enableResetPasswordV2"
  | "disableModalAddressFromBureau"
  | "enableReportButton"
  | "enableConsignedAmigozColor"
  | "enableOperationalGuide"
  | "enableMandatoryCourses"
  | "enableValidateAddress"
  | "enableInsuranceWithdrawal"
  | "enableblockCEP"
  | "enableResidualBalance"
  | "enableInsuranceRefin"
  | "enableNuvideoRedirect"
  | "enableManageUsers"
  | "disableRecaptcha"
  | "enableBureau"
  | "enableTeimosinhaRepurchase";

export type FeatureFlagsKeys =
  | FeatureFlagsKeysFromEnv
  | FeatureFlagsKeysFromApi;

/**
 * Parses a feature flag key located in the environment variables
 * @returns Whether the feature flag is active
 */
function flag(key: keyof ImportMetaEnv): boolean {
  return env[key] === "1";
}

/**
 * Feature flags from env
 */
export const featureFlagsFromEnv: Record<FeatureFlagsKeysFromEnv, boolean> = {
  enableAggregateOfferDocumentType: flag(
    "VITE_ENABLE_AGGREGATE_OFFER_DOCUMENT_TYPE",
  ),
  enableCorrespondenteBanqueiro: flag("VITE_ENABLE_CORRESPONDENTE_BANQUEIRO"),
  enableImageDocumentsRequirementsToUpload: flag(
    "VITE_ENABLE_IMAGE_DOCUMENTS_REQUIREMENTS_TO_UPLOAD",
  ),
  enableSimulationSelectFieldInstallments: flag(
    "VITE_ENABLE_SIMULATION_SELECT_FIELD_INSTALLMENTS",
  ),
  /* document status column */
  enableContractsDocumentStatus: flag("VITE_ENABLE_CONTRACTS_DOCUMENT_STATUS"),
  enableContractsOrderTitle: flag("VITE_ENABLE_CONTRACTS_ORDER_TITLE"),
  enableValidationCRCP: flag("VITE_ENABLE_VALIDATION_CRCP"),
  enableDigitalBanks: flag("VITE_ENABLE_DIGITAL_BANKS"),
  enableDocumentsUploadType: flag("VITE_ENABLE_DOCUMENTS_UPLOAD_TYPE"),
  enableDinamicMarginInss: flag("VITE_ENABLE_DINAMIC_MARGIN_INSS"),
  enableHistoryCRCP: flag("VITE_ENABLE_QUERY_HISTORY_CRCP"),
};

/**
 * Feature flags from api
 * @key is the name of the feature flag
 * @value is the name of the api flag
 */
const featureFlagsFromApi: Record<
  FeatureFlagsKeysFromApi,
  | NonNullable<IApiFeatureFlagsResponse>[number]["name"]
  | Array<NonNullable<IApiFeatureFlagsResponse>[number]["name"]>
> = {
  enableValidationCRCP: "crcp",
  enableDigitalBanks: [
    // "bancos_digitais_validacao",
    "bancos_digitais_parametros",
  ],
  enableDocumentsUploadType: "dossie-trava-jpeg",
  enableDinamicMarginInss: "margem-dinamica-inss",
  enableValidationMCB: "mcb",
  enableBypassValidaCPF: "front-bypass-valida-cpf-boleto",
  enableCreateContractBff: "criar-contrato-bff",
  enableIgnoreMarginError: "front-ignora-margem-erros",
  enableResetPasswordV2: "front-reset-senha",
  disableModalAddressFromBureau: "front-ignora-enderecos-bureau",
  enableReportButton: "front-ativa-botao-relatorio",
  enableConsignedAmigozColor: "front-cores-amigoz-consignado",
  enableOperationalGuide: "front-roteiros-operacionais",
  enableMandatoryCourses: "front-jornada-curso-obrigatorio-corban",
  enableValidateAddress: "validacao_endereco",
  enableInsuranceWithdrawal: "front-seguro-saque-complementar",
  enableblockCEP: "front-bloqueio-cep",
  enableResidualBalance: "saldo_residual",
  enableInsuranceRefin: "seguros_refin",
  enableNuvideoRedirect: "nuvidio-redirect-formalization",
  enableManageUsers: "corban-gestao-usuarios",
  disableRecaptcha: "front-desabilita-recaptcha",
  enableBureau: "front-habilita-bureau",
  enableTeimosinhaRepurchase: "furafila-compra-cliente",
};

const getFeatureFlagValue = (
  flagKey: string | Array<string>,
  flags: NonNullable<IApiFeatureFlagsResponse>,
  defaultValue = false,
): boolean => {
  if (flagKey instanceof Array) {
    return !flagKey.some(
      (key) => !getFeatureFlagValue(key, flags, defaultValue),
    );
  }

  const flag = flags.find(({ name }) => name === flagKey);
  return flag?.is_active ?? defaultValue;
};

export const mapFeatureFlagsToBoolean = (
  featureFlagsResponse: IApiFeatureFlagsResponse | undefined,
): { [key in FeatureFlagsKeys]: boolean } => {
  return Object.entries(featureFlagsFromApi).reduce(
    (result, [flag, flagKey]) => {
      if (flag in featureFlagsFromApi) {
        result[flag as FeatureFlagsKeys] = getFeatureFlagValue(
          flagKey,
          featureFlagsResponse ?? [],
          !!featureFlagsFromEnv?.[flag as FeatureFlagsKeysFromEnv],
        );
      }
      return result;
    },
    featureFlagsFromEnv as { [key in FeatureFlagsKeys]: boolean },
  );
};
